import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Users, 
  Calculator, 
  FileText, 
  Settings, 
  LogOut, 
  Zap,
  ChevronLeft,
  ChevronRight,
  PiggyBank,
  Receipt,
  UserPlus,
  Calendar,
  Route,
  BarChart3,
  Shield,
  PenTool,
  Euro
} from "lucide-react";

const Sidebar = ({ user, logout }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // Build navigation array
  const baseNavigation = [
    // Dashboard pour tous sauf Telepro
    { name: "Dashboard", href: "/", icon: LayoutDashboard, roles: ["admin", "commercial", "regie", "administratif"] },
    
    // Prospects - pour les Telepro et Admin (vision globale)
    { name: "Prospects", href: "/prospects", icon: UserPlus, roles: ["telepro", "admin"] },
    
    // Planning - pour Telepro, Commercial, Régie, Admin, Administratif
    { name: "Planning", href: "/planning", icon: Calendar, roles: ["telepro", "commercial", "regie", "admin", "administratif"] },
    
    // Distribution RDV - uniquement pour Admin et Administratif
    { name: "Distribution RDV", href: "/distribution", icon: Route, roles: ["admin", "administratif"] },
    
    // Statistiques - uniquement pour Admin
    { name: "Statistiques", href: "/statistiques", icon: BarChart3, roles: ["admin"] },
    
    // Autres onglets existants (pas accessibles aux Telepro)
    { name: "Clients", href: "/clients", icon: Users, roles: ["admin", "commercial", "regie", "administratif"] },
    { name: "Simulateur", href: "/simulator", icon: Calculator, roles: ["admin", "commercial"] },
    { name: "Contrats", href: "/contracts", icon: FileText, roles: ["admin", "regie", "administratif"] },
    { name: "Devis & Factures", href: "/devis-factures", icon: Receipt, roles: ["admin", "administratif"] },
    { name: "Trésorerie", href: "/treasury", icon: PiggyBank, roles: ["admin"] },
    
    // NOUVEAU: Mes Commissions - pour commercial, régie, telepro
    { name: "Mes Commissions", href: "/my-commissions", icon: Euro, roles: ["commercial", "regie", "telepro"] },
    
    // Signature ACD - pour commercial, admin, super_admin, administratif
    { name: "Signature ACD", href: "/signature-acd", icon: PenTool, roles: ["commercial", "admin", "administratif"] },
    
    { name: "Paramètres", href: "/settings", icon: Settings, roles: ["admin"] },
  ];

  // Add Audit Logs for super admin only
  const navigation = user?.is_super_admin
    ? [...baseNavigation, { name: "Audit & Logs", href: "/audit-logs", icon: Shield, roles: ["admin"] }]
    : baseNavigation;

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: "bg-red-100 text-red-800",
      commercial: "bg-blue-100 text-blue-800", 
      regie: "bg-green-100 text-green-800",
      administratif: "bg-purple-100 text-purple-800",
      telepro: "bg-orange-100 text-orange-800"
    };
    return colors[role] || "bg-gray-100 text-gray-800";
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: "Administrateur",
      commercial: "Commercial",
      regie: "Régie", 
      administratif: "Administratif",
      telepro: "Telepro"
    };
    return labels[role] || role;
  };

  return (
    <div className={`${collapsed ? 'w-16' : 'w-64'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 shadow-sm`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          {!collapsed && (
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_ccca3806-d8f7-44f9-8ff2-bbd147115d9d/artifacts/5h8z3m6s_Logo%20Easypower.png"
                alt="Easy Power"
                className="w-8 h-8 object-contain"
              />
              <div>
                <h2 className="text-lg font-bold text-slate-800">Easy Power</h2>
                <p className="text-xs text-emerald-600 font-medium">CRM Énergie</p>
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 hover:bg-gray-100"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* User Info */}
      {!collapsed && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-medium">
              {user.first_name?.[0]}{user.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleColor(user.role)} mt-1`}>
                {getRoleLabel(user.role)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navigation
          .filter(item => item.roles.includes(user.role))
          .map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? "bg-emerald-50 text-emerald-700 border-r-2 border-emerald-600"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className={`w-5 h-5 ${active ? "text-emerald-600" : ""}`} />
                {!collapsed && <span>{item.name}</span>}
              </Link>
            );
          })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <Button
          variant="ghost"
          onClick={logout}
          className={`w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700 ${collapsed ? 'px-2' : ''}`}
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="ml-3">Déconnexion</span>}
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;